import { create } from 'zustand';
import type {
  Channel,
  ConversationListEntry,
  Message,
  Reaction,
  User,
  WorkspaceUser,
} from './types';

interface ChatState {
  me: User | null;
  users: WorkspaceUser[];
  conversations: ConversationListEntry[];
  selectedConversationId: string | null;
  messagesByConv: Record<string, Message[]>;
  hasMoreByConv: Record<string, boolean>;
  // Per-conversation: array of userIds currently typing (excluding self).
  typingByConv: Record<string, string[]>;
  // Per-user-per-conversation: messageId most recently read by that user.
  readByConv: Record<string, Record<string, string>>;
  // Workspace presence — overrides users[].online when an event arrives.
  onlineUserIds: Record<string, boolean>;
  // Per-conversation: the message currently being replied to (or null).
  replyingByConv: Record<string, Message | null>;
  // Set of message IDs the current user has starred (across all conversations).
  // Loaded lazily; falls back to "not starred" when the entry is missing.
  starredMessageIds: Record<string, true>;
  // Set of message IDs that are currently pinned in their conversation.
  // Conversation-scope is implicit (a message id is unique system-wide);
  // hydrated when the PinnedStrip fetches /:id/pinned and updated by
  // socket pin/unpin events.
  pinnedMessageIds: Record<string, true>;
  // Pending jump request — set when something (e.g. starred-list click) wants
  // the message list to scroll a specific message into view and flash it.
  // Cleared by the MessageList after it consumes the request.
  jumpToMessageId: string | null;
  // Distinguishes "still loading the initial conversation list" from "loaded
  // and the user has zero conversations" so the sidebar can render skeletons
  // during the gap instead of flashing the empty state.
  conversationsLoaded: boolean;
  // Workspace channels the user can see, sorted by sortOrder. The
  // default ("General") is always at the top.
  channels: Channel[];
  // Currently-active channel filter. null = "All chats" (every conv);
  // a channel id filters the sidebar to that channel's conversations.
  activeChannelId: string | null;
}

interface ChatActions {
  setMe: (user: User | null) => void;
  setUsers: (users: WorkspaceUser[]) => void;
  setConversations: (convs: ConversationListEntry[]) => void;
  upsertConversation: (conv: ConversationListEntry) => void;
  applyConversationUpdate: (
    id: string,
    fields: {
      name?: string | null;
      topic?: string | null;
      avatarUpdatedAt?: string | null;
      mutedUntil?: string | null;
      advancedPrivacy?: boolean;
      participants?: ConversationListEntry['participants'];
      bridge?: ConversationListEntry['bridge'];
    },
  ) => void;
  removeConversation: (id: string) => void;
  selectConversation: (id: string | null) => void;
  setMessages: (convId: string, messages: Message[], hasMore: boolean) => void;
  prependMessages: (convId: string, messages: Message[], hasMore: boolean) => void;
  upsertMessage: (msg: Message) => void;
  markMessageDeleted: (convId: string, msgId: string, deletedAt: string) => void;
  addReaction: (convId: string, msgId: string, userId: string, emoji: string) => void;
  removeReaction: (convId: string, msgId: string, userId: string, emoji: string) => void;
  setTyping: (convId: string, userId: string, typing: boolean) => void;
  setPresence: (userId: string, online: boolean) => void;
  setReadReceipt: (convId: string, userId: string, msgId: string) => void;
  bumpConversationLastMessage: (msg: Message) => void;
  setReplyingTo: (convId: string, msg: Message | null) => void;
  setStarredIds: (ids: string[]) => void;
  toggleStarLocal: (msgId: string, starred: boolean) => void;
  setPinnedIds: (ids: string[]) => void;
  togglePinLocal: (msgId: string, pinned: boolean) => void;
  setChannels: (channels: Channel[]) => void;
  upsertChannel: (channel: Channel) => void;
  removeChannel: (id: string) => void;
  setActiveChannelId: (id: string | null) => void;
  setConversationChannel: (convId: string, channelId: string | null) => void;
  setJumpToMessage: (id: string | null) => void;
  reset: () => void;
}

const initialState: ChatState = {
  me: null,
  users: [],
  conversations: [],
  selectedConversationId: null,
  messagesByConv: {},
  hasMoreByConv: {},
  typingByConv: {},
  readByConv: {},
  onlineUserIds: {},
  replyingByConv: {},
  starredMessageIds: {},
  pinnedMessageIds: {},
  jumpToMessageId: null,
  conversationsLoaded: false,
  channels: [],
  activeChannelId: null,
};

export const useChat = create<ChatState & ChatActions>((set) => ({
  ...initialState,

  setMe: (user) => set({ me: user }),

  setUsers: (users) =>
    set(() => {
      const onlineUserIds: Record<string, boolean> = {};
      for (const u of users) onlineUserIds[u.id] = u.online;
      return { users, onlineUserIds };
    }),

  setConversations: (conversations) => {
    set(() => {
      const readByConv: Record<string, Record<string, string>> = {};
      for (const c of conversations) {
        if (c.lastReadId) {
          readByConv[c.id] = { ['__self_marker__']: c.lastReadId };
        }
      }
      return { conversations, readByConv, conversationsLoaded: true };
    });
  },

  upsertConversation: (conv) =>
    set((s) => {
      const idx = s.conversations.findIndex((c) => c.id === conv.id);
      const next = [...s.conversations];
      if (idx === -1) next.unshift(conv);
      else next[idx] = conv;
      return { conversations: next };
    }),

  applyConversationUpdate: (id, fields) =>
    set((s) => {
      const idx = s.conversations.findIndex((c) => c.id === id);
      if (idx === -1) return s;
      const updated: ConversationListEntry = {
        ...s.conversations[idx]!,
        ...(fields.name !== undefined ? { name: fields.name } : {}),
        ...(fields.topic !== undefined ? { topic: fields.topic } : {}),
        ...(fields.avatarUpdatedAt !== undefined
          ? { avatarUpdatedAt: fields.avatarUpdatedAt }
          : {}),
        ...(fields.mutedUntil !== undefined ? { mutedUntil: fields.mutedUntil } : {}),
        ...(fields.advancedPrivacy !== undefined
          ? { advancedPrivacy: fields.advancedPrivacy }
          : {}),
        ...(fields.participants !== undefined
          ? { participants: fields.participants }
          : {}),
        ...(fields.bridge !== undefined ? { bridge: fields.bridge } : {}),
      };
      const next = [...s.conversations];
      next[idx] = updated;
      return { conversations: next };
    }),

  removeConversation: (id) =>
    set((s) => ({
      conversations: s.conversations.filter((c) => c.id !== id),
      selectedConversationId:
        s.selectedConversationId === id ? null : s.selectedConversationId,
    })),

  selectConversation: (id) => set({ selectedConversationId: id }),

  setMessages: (convId, messages, hasMore) =>
    set((s) => ({
      messagesByConv: { ...s.messagesByConv, [convId]: messages },
      hasMoreByConv: { ...s.hasMoreByConv, [convId]: hasMore },
    })),

  prependMessages: (convId, messages, hasMore) =>
    set((s) => ({
      messagesByConv: {
        ...s.messagesByConv,
        [convId]: [...messages, ...(s.messagesByConv[convId] ?? [])],
      },
      hasMoreByConv: { ...s.hasMoreByConv, [convId]: hasMore },
    })),

  upsertMessage: (msg) =>
    set((s) => {
      const existing = s.messagesByConv[msg.conversationId] ?? [];
      const idx = existing.findIndex((m) => m.id === msg.id);
      let updated: Message[];
      if (idx === -1) updated = [...existing, msg];
      else {
        updated = [...existing];
        updated[idx] = msg;
      }
      return {
        messagesByConv: { ...s.messagesByConv, [msg.conversationId]: updated },
      };
    }),

  markMessageDeleted: (convId, msgId, deletedAt) =>
    set((s) => {
      const existing = s.messagesByConv[convId] ?? [];
      const updated = existing.map((m) =>
        m.id === msgId ? { ...m, body: null, deletedAt } : m,
      );
      return { messagesByConv: { ...s.messagesByConv, [convId]: updated } };
    }),

  addReaction: (convId, msgId, userId, emoji) =>
    set((s) => {
      const existing = s.messagesByConv[convId] ?? [];
      const updated = existing.map((m) => {
        if (m.id !== msgId) return m;
        const reactions = [...(m.reactions ?? [])];
        const idx = reactions.findIndex((r) => r.emoji === emoji);
        if (idx === -1) {
          reactions.push({ emoji, userIds: [userId] });
        } else if (!reactions[idx]!.userIds.includes(userId)) {
          reactions[idx] = {
            ...reactions[idx]!,
            userIds: [...reactions[idx]!.userIds, userId],
          };
        }
        return { ...m, reactions };
      });
      return { messagesByConv: { ...s.messagesByConv, [convId]: updated } };
    }),

  removeReaction: (convId, msgId, userId, emoji) =>
    set((s) => {
      const existing = s.messagesByConv[convId] ?? [];
      const updated = existing.map((m) => {
        if (m.id !== msgId) return m;
        const reactions = (m.reactions ?? [])
          .map((r) =>
            r.emoji === emoji
              ? { ...r, userIds: r.userIds.filter((id) => id !== userId) }
              : r,
          )
          .filter((r) => r.userIds.length > 0);
        return { ...m, reactions };
      });
      return { messagesByConv: { ...s.messagesByConv, [convId]: updated } };
    }),

  setTyping: (convId, userId, typing) =>
    set((s) => {
      const current = s.typingByConv[convId] ?? [];
      let next: string[];
      if (typing) {
        if (current.includes(userId)) return s;
        next = [...current, userId];
      } else {
        if (!current.includes(userId)) return s;
        next = current.filter((id) => id !== userId);
      }
      return { typingByConv: { ...s.typingByConv, [convId]: next } };
    }),

  setPresence: (userId, online) =>
    set((s) => ({
      onlineUserIds: { ...s.onlineUserIds, [userId]: online },
    })),

  setReadReceipt: (convId, userId, msgId) =>
    set((s) => ({
      readByConv: {
        ...s.readByConv,
        [convId]: { ...(s.readByConv[convId] ?? {}), [userId]: msgId },
      },
    })),

  setReplyingTo: (convId, msg) =>
    set((s) => ({
      replyingByConv: { ...s.replyingByConv, [convId]: msg },
    })),

  setStarredIds: (ids) =>
    set((s) => {
      const map = { ...s.starredMessageIds };
      for (const id of ids) map[id] = true;
      return { starredMessageIds: map };
    }),

  toggleStarLocal: (msgId, starred) =>
    set((s) => {
      const next = { ...s.starredMessageIds };
      if (starred) next[msgId] = true;
      else delete next[msgId];
      return { starredMessageIds: next };
    }),

  setPinnedIds: (ids) =>
    set((s) => {
      const map = { ...s.pinnedMessageIds };
      for (const id of ids) map[id] = true;
      return { pinnedMessageIds: map };
    }),

  togglePinLocal: (msgId, pinned) =>
    set((s) => {
      const next = { ...s.pinnedMessageIds };
      if (pinned) next[msgId] = true;
      else delete next[msgId];
      return { pinnedMessageIds: next };
    }),

  setChannels: (channels) => set({ channels }),
  upsertChannel: (channel) =>
    set((s) => {
      const idx = s.channels.findIndex((c) => c.id === channel.id);
      const next = [...s.channels];
      if (idx === -1) next.push(channel);
      else next[idx] = channel;
      // Keep sortOrder canonical so the icon-nav order matches the
      // server. Default-flagged channels (sortOrder 0 from migration)
      // naturally float to top.
      next.sort((a, b) => a.sortOrder - b.sortOrder);
      return { channels: next };
    }),
  removeChannel: (id) =>
    set((s) => ({
      channels: s.channels.filter((c) => c.id !== id),
      // If the removed channel was the active filter, drop the filter
      // so the user lands back on "All chats" rather than a stale empty
      // view.
      activeChannelId: s.activeChannelId === id ? null : s.activeChannelId,
    })),
  setActiveChannelId: (id) => set({ activeChannelId: id }),
  setConversationChannel: (convId, channelId) =>
    set((s) => {
      const idx = s.conversations.findIndex((c) => c.id === convId);
      if (idx === -1) return s;
      const next = [...s.conversations];
      next[idx] = { ...next[idx]!, channelId };
      return { conversations: next };
    }),

  setJumpToMessage: (id) => set({ jumpToMessageId: id }),

  bumpConversationLastMessage: (msg) =>
    set((s) => {
      const idx = s.conversations.findIndex((c) => c.id === msg.conversationId);
      if (idx === -1) return s;
      const updated: ConversationListEntry = {
        ...s.conversations[idx]!,
        lastMessage: {
          id: msg.id,
          senderId: msg.senderId,
          body: msg.body,
          createdAt: msg.createdAt,
          deleted: msg.deletedAt !== null,
          kind: msg.kind,
        },
        unreadCount:
          msg.senderId === s.me?.id || s.selectedConversationId === msg.conversationId
            ? 0
            : (s.conversations[idx]!.unreadCount ?? 0) + 1,
      };
      const next = [...s.conversations];
      next.splice(idx, 1);
      next.unshift(updated);
      return { conversations: next };
    }),

  reset: () => set(initialState),
}));

// Helper selectors
export const selectActiveConversation = (s: ChatState): ConversationListEntry | undefined =>
  s.conversations.find((c) => c.id === s.selectedConversationId);

export function reactionUsersAreCurrentUser(reaction: Reaction, userId: string | undefined): boolean {
  return userId !== undefined && reaction.userIds.includes(userId);
}
